<section class="service-page">
  <div class="row pad">
    <div class="service-page__inner">
      <h1>Supplier Code of Conduct</h1>

      <h2>Introduction</h2>
      <p>
        MARI is reshaping the live events landscape — delivering experiences
        that move culture globally. Delivering these experiences depends on a
        broad and diverse network of venues, contractors, ride operators,
        hospitality providers, sponsors, agencies, licensors, technology
        providers, and other business partners.
      </p>
      <p>
        This Supplier Code of Conduct (this "Code") reflects the standards MARI
        expects of every Supplier that does business with us or on our behalf.
        It is built on the same principles of integrity, respect, and
        accountability set out in the MARI Code of Conduct, and exists so that
        the trust our audiences, partners, and communities place in MARI is
        honored throughout our supply chain.
      </p>
      <p>
        We ask every Supplier to read this Code carefully, apply it in daily
        operations, and raise questions or concerns promptly using the Reporting
        Channels described in Part VI.
      </p>
      <p>
        This Code of Conduct supplements, and does not replace, the terms of any
        written Agreement between a Supplier and MARI. Where this Code and an
        Agreement conflict, the Agreement controls; where this Code and
        Applicable Law address the same subject, the stricter standard governs,
        except where doing so would itself violate Applicable Law.
      </p>

      <h2>Part I – Purpose, Applicability, and Implementation</h2>

      <h2>1. Purpose</h2>
      <p>
        MARI is committed to conducting business with honesty, integrity, and in
        compliance with the law everywhere it operates. This commitment extends
        to every organization and individual that supplies goods or services to
        MARI. This Code sets out the minimum standards of ethical, legal, and
        operational conduct that MARI requires of its Suppliers across business
        integrity, labor and human rights, health and safety, data protection,
        environmental responsibility, and supply chain oversight.
      </p>
      <p>
        This Code supplements, and does not replace, the specific terms of any
        master services agreement, purchase order, statement of work, or other
        written contract between a Supplier and MARI (each, an "Agreement").
      </p>

      <h2>2. Definitions</h2>
      <p>
        Capitalized terms used in this Code have the meanings given in Part VII
        (Glossary of Terms) or where first defined in the text.
      </p>

      <h2>3. Who This Code Applies To</h2>
      <p>
        This Code applies to every Supplier engaged by MARI or any of its
        subsidiaries or controlled affiliates worldwide, including:
      </p>
      <ul>
        <li>vendors, contractors, and independent service providers;</li>
        <li>
          ride operators, hospitality providers, art galleries, writers, video
          producers, sponsors, and event security partners;
        </li>
        <li>
          agents, consultants, brokers, and other Third-Party Intermediaries
          acting on MARI's behalf;
        </li>
        <li>
          venues, production companies, staffing agencies, security providers,
          and technology and software providers; and
        </li>
        <li>
          any Subcontractor engaged by a Supplier to perform work related to a
          MARI Agreement, to the extent set out in Part VI, Section 2.
        </li>
      </ul>
      <p>
        For purposes of this Code, "MARI," "the Company," or "we" refers to AE
        EventsCo Holdings LLC and its subsidiaries and directly and indirectly
        controlled affiliates operating under any MARI or affiliate brand, and
        "Supplier" refers to any entity or individual described in this Section
        3.
      </p>
      <p>
        Delivering MARI’s events requires close coordination with venues,
        artists and talent representatives, sponsors, security and staffing
        providers, production and technical vendors, hospitality and travel
        providers, ticketing and technology platforms, and licensing and customs
        partners. Because MARI's reputation depends on the conduct of everyone
        associated with its events, this Code applies equally to Suppliers
        regardless of size, location, or the value of their engagement.
      </p>

      <h2>4. Compliance with Law</h2>
      <p>
        Suppliers must comply with all Applicable Law in every jurisdiction
        where they operate or perform work for MARI, and with this Code. Where
        this Code and Applicable Law address the same subject, the Supplier must
        follow whichever standard is stricter, except where doing so would
        itself violate Applicable Law.
      </p>

      <h2>5. Implementation of this Code</h2>
      <p>
        Commensurate with their size and the nature of their business
        operations, Suppliers must implement appropriate and reasonable
        processes, controls, and policies to implement and comply with this Code
        and all Applicable Law. Suppliers must provide their personnel, both
        employees and contractors, with a means to report violations of
        Applicable Law and of this Code. Suppliers must also ensure that any good
        faith report made is not subject to retaliation.
      </p>

      <h2>Part II – Business Integrity and Ethical Conduct</h2>

      <h2>6. General Standard of Conduct</h2>
      <p>
        Suppliers must conduct business honestly, fairly, and in compliance with
        Applicable Law, and must not engage in any activity that could damage
        MARI's reputation or the trust of its guests, partners, artists,
        sponsors, or audiences.
      </p>

      <h2>7. Anti-Bribery and Anti-Corruption</h2>
      <p>
        Suppliers must not, directly or indirectly, offer, promise, give,
        request, or accept any Anything of Value to or from any person,
        including (but not limited to) a Government Official, in order to
        improperly influence a decision, obtain or retain business, or secure
        any improper advantage for MARI, the Supplier, or any other party.
      </p>
      <p>
        This prohibition applies to dealings with Government Officials and
        private-sector counterparties alike and applies regardless of local
        custom or whether the payment or benefit is made directly or through a
        Third-Party Intermediary. Suppliers acting on MARI's behalf, or whose
        work involves interaction with Government Officials in connection with a
        MARI engagement, must comply with the U.S. Foreign Corrupt Practices Act
        (15 U.S.C. §§ 78dd-1 et seq.), the UK Bribery Act 2010, and other
        applicable anti-corruption laws.
      </p>

      <h2>8. Gifts and Business Hospitality</h2>
      <p>
        Gifts and Business Hospitality exchanged in connection with MARI
        business must have a legitimate business purpose, be reasonable in value
        and frequency, comply with Applicable Law, and never be offered, given,
        or accepted with the intent, or appearance, of improperly influencing a
        business decision. Suppliers must not provide any Gift or Business
        Hospitality to a Government Official in connection with MARI business
        without MARI's prior written approval.
      </p>

      <h2>9. Conflicts of Interest</h2>
      <p>
        Suppliers must disclose to their MARI contact any relationship,
        financial interest, or other circumstance that creates, or could
        reasonably appear to create, a conflict between the Supplier's interests
        and MARI's interests, including any Immediate Family Member relationship
        or Significant Financial Interest involving a MARI employee. Suppliers
        must also disclose any relationship or other circumstance that creates,
        or could reasonably appear to create, an unfair advantage with a
        government or government agency.
      </p>

      <h2>10. Fair Competition and Antitrust</h2>
      <p>
        Suppliers must compete on the merits of their products and services and
        comply with all applicable antitrust and competition laws. Suppliers
        must not enter into any agreement or understanding with a competitor of
        MARI, or of the Supplier, concerning pricing, market or customer
        allocation, bid-rigging, or other competitively sensitive terms in
        connection with MARI business.
      </p>

      <h2>
        11. Anti-Money Laundering, Sanctions, Export Controls, and Anti-Boycott
        Compliance
      </h2>
      <p>
        Suppliers must not engage, directly or indirectly, in money laundering
        or terrorist financing, and must comply with Applicable Law relating to
        anti-money laundering, economic sanctions, export controls, and import
        controls, including regulations administered by the U.S. Department of
        the Treasury's Office of Foreign Assets Control (31 C.F.R. Parts
        500–599), the U.S. Department of Commerce's Export Administration
        Regulations (15 C.F.R. Parts 730–774), and equivalent regimes in the
        other jurisdictions in which the Supplier operates.
      </p>
      <p>
        Suppliers must not be, and must not engage any Subcontractor, agent, or
        other party that is, a Sanctioned Party, and must promptly notify the
        MARI Legal Department if the Supplier becomes aware of any change in its
        ownership, control, or operations that could affect its sanctions
        status.
      </p>
      <p>
        Suppliers must not participate in, and must promptly report to the MARI
        Legal Department, any request to support or comply with an unsanctioned
        foreign boycott, including requests implicating the U.S. anti-boycott
        regulations (15 C.F.R. Part 760) or Section 999 of the U.S. Internal
        Revenue Code (26 U.S.C. § 999).
      </p>

      <h2>12. Government Interactions and Political Activities</h2>
      <p>
        Suppliers interacting with Government Officials or Government Entities
        on MARI's behalf must comply with all Applicable Law governing such
        interactions, including procurement, licensing, permitting, and
        disclosure requirements relevant to live events. Suppliers must not use
        MARI's name, funds, or resources to make political contributions or
        engage in lobbying on MARI's behalf without MARI's prior written
        approval.
      </p>

      <h2>13. Accurate Books, Records, and Financial Integrity</h2>
      <p>
        Suppliers must maintain accurate and complete books, records, invoices,
        and supporting documentation for all transactions related to MARI
        business, and must not create false or misleading records, conceal
        information, or facilitate any inaccurate reporting by MARI, consistent
        with the recordkeeping expectations of the FCPA's books-and-records
        provisions, the UK Bribery Act 2010, and, where applicable, the U.S.
        Sarbanes-Oxley Act of 2002 and the UK Companies Act 2006.
      </p>

      <h2>14. Confidential and Non-Public Information</h2>
      <p>
        Suppliers must not use or disclose confidential or non-public
        information obtained through a MARI engagement, including material
        non-public information about MARI or its partners, for personal gain,
        securities trading, or any purpose outside the scope of the Supplier's
        engagement.
      </p>

      <h2>Part III – Human Rights and Workplace Standards</h2>

      <h2>15. Fair Employment and Non-Discrimination</h2>
      <p>
        Suppliers must make employment decisions based on qualifications,
        skills, experience, and performance, and must not discriminate on the
        basis of race, color, religion, national origin, sex, gender identity or
        expression, sexual orientation, age, disability, veteran status, or any
        other characteristic protected by Applicable Law.
      </p>

      <h2>16. Freely Chosen Employment</h2>
      <p>
        Suppliers must not use forced, bonded, indentured, prison, or
        involuntary labor, or engage in human trafficking, in any part of their
        operations or supply chains. Workers must be free to leave employment
        upon reasonable notice, and Suppliers must not withhold identity or
        immigration documents or charge workers recruitment fees as a condition
        of employment. To the extent applicable, Suppliers must comply with laws
        addressing Modern Slavery. Where Suppliers may allow volunteers to work
        on an event, the Supplier must ensure that such volunteer work is
        consistent with Applicable Law and this Code.
      </p>

      <h2>17. Prohibition on Child Labor</h2>
      <p>
        Suppliers must not employ (whether for payment or as a volunteer), or
        use as a contractor, any person under the minimum legal working age in
        the jurisdiction of employment or under the age of 15, whichever is
        older (higher). Where local law permits legitimate youth apprenticeship
        or internship programs, Suppliers must ensure such programs protect the
        participant's health, safety, and education.
      </p>

      <h2>18. Wages, Benefits, and Working Hours</h2>
      <p>
        Suppliers must pay workers at least the minimum wage and provide the
        benefits required by Applicable Law, compensate overtime at the legally
        required rate, and ensure working hours comply with Applicable Law and
        any limits set out in the Supplier's Agreement with MARI. Suppliers must
        comply with all Applicable Law involving employment and labor relevant
        to the services that they are supplying to MARI.
      </p>

      <h2>19. Health, Safety, and Event Security</h2>
      <p>
        Given the nature of live events, Suppliers whose personnel work at MARI
        venues, productions, or events must maintain a safe working environment,
        comply with Applicable Law governing occupational health and safety, and
        follow the site-specific safety, security, and crowd-management
        protocols established by MARI or the venue for the relevant event.
        Suppliers must promptly report accidents, injuries, unsafe conditions,
        or security incidents to their MARI contact or through the Reporting
        Channels described in Part VI, Section 30.
      </p>

      <h2>20. Harassment-Free and Respectful Workplace</h2>
      <p>
        Suppliers must maintain a workplace free from harassment, bullying,
        corporal punishment, and physical, sexual, psychological, or verbal
        abuse, and must ensure that personnel working on MARI engagements treat
        MARI employees, artists, partners, and attendees with respect.
      </p>

      <h2>
        Part IV – Data Protection, Information Security, and Intellectual
        Property
      </h2>

      <h2>21. Data Privacy and Protection of Personal Data</h2>
      <p>
        Suppliers that Process Personal Data in connection with MARI business
        must comply with Applicable Law governing data protection and privacy,
        including, where applicable, the EU/UK General Data Protection
        Regulation, the California Consumer Privacy Act, as amended by the
        California Privacy Rights Act, and any other law governing the handling
        of Personal Data (collectively, “Applicable Data Protection Law”).
      </p>
      <p>Suppliers shall:</p>
      <ul>
        <li>
          only use Personal Data for the purpose for which it was collected or
          as may be required by Applicable Law;
        </li>
        <li>
          adhere to the applicable privacy notice that governs the Personal Data
          that Supplier is Processing;
        </li>
        <li>
          implement appropriate technical and organizational measures to protect
          Personal Data and adopt processes intended to ensure that such
          measures remain appropriate over time;
        </li>
        <li>
          respect the rights of Data Subjects under Applicable Data Protection
          Law to access, correct, update, delete, object to the processing of,
          and/or obtain a copy of their Personal Data, and provide assistance to
          MARI, where applicable, in MARI’s efforts to respect the rights of
          Data Subjects;
        </li>
        <li>
          ensure that any international transfer of Personal Data is consistent
          with Applicable Data Protection Law, including, where applicable,
          standard contractual clauses or another means of authorized transfer
          of Personal Data; and
        </li>
        <li>
          ensure that any direct marketing and/or targeted advertising is
          conducted in adherence with Applicable Law and provides for opt in
          consent (where applicable) and the ability to opt out or unsubscribe
          in all circumstances.
        </li>
      </ul>

      <h2>22. Information and Cyber Security</h2>
      <p>
        Suppliers with access to MARI's systems, networks, or Information Assets
        must maintain a risk-based information security program appropriate to
        the sensitivity of the data and systems involved, must comply with
        MARI's Third-Party Risk Management Policy and Information Security
        Policy where incorporated into the Supplier's Agreement, and must
        promptly notify MARI at
        <a href="mailto:helpdesk@marigrp.com">helpdesk&#64;marigrp.com</a> of
        any actual or suspected data breach, cybersecurity incident, or
        unauthorized access to MARI systems or data.
      </p>

      <h2>23. Intellectual Property and Confidentiality</h2>
      <p>
        Suppliers must respect MARI's Intellectual Property and that of MARI's
        artists, sponsors, venues, and other partners, and must use Restricted
        Information disclosed by MARI only for the purpose of the Supplier's
        engagement and only with individuals who have a legitimate need to know.
        Suppliers must not disclose Restricted Information outside their
        organization without MARI's prior written authorization and, where
        applicable, an executed confidentiality agreement.
      </p>

      <h2>24. Protection of Company Assets</h2>
      <p>
        Suppliers granted access to MARI facilities, equipment, systems, or
        other assets must use those assets responsibly, only for purposes
        related to the Supplier's engagement, and must take reasonable steps to
        prevent their loss, theft, misuse, or unauthorized disclosure.
      </p>

      <h2>Part V – Environmental Responsibility and Sustainable Events</h2>

      <h2>25. Environmental Compliance</h2>
      <p>
        Suppliers must comply with all Applicable Law governing environmental
        protection, including permitting, waste disposal, and emissions
        requirements applicable to their operations and to the venues and sites
        where they perform work for MARI.
      </p>

      <h2>26. Sustainable Event Practices</h2>
      <p>
        Where relevant to their engagement, Suppliers should support MARI's
        efforts to reduce the environmental impact of its events, including by
        minimizing waste and single-use materials, using energy-efficient
        equipment and practices where feasible, and cooperating with MARI's
        sustainability initiatives at event sites. MARI may request information
        from Suppliers regarding the environmental impact of goods or services
        provided in connection with a MARI event.
      </p>

      <h2>Part VI – Supply Chain Governance, Monitoring, and Enforcement</h2>

      <h2>27. Onboarding and Risk-Based Due Diligence</h2>
      <p>
        MARI evaluates Suppliers for legal, financial, security, and
        reputational risk before onboarding and periodically throughout the
        relationship, consistent with MARI's Third-Party Risk Management Policy.
        Suppliers must provide accurate and complete information in response to
        MARI's due diligence requests, including screening related to
        sanctions, export controls, and anti-corruption risk, and must promptly
        notify MARI of any material change to that information.
      </p>

      <h2>28. Subcontractors and Flow-Down</h2>
      <p>
        Suppliers must ensure that any Subcontractor engaged to perform work
        directly related to a MARI Agreement is bound to standards materially
        consistent with this Code and remain responsible for that
        Subcontractor's compliance. Suppliers must obtain MARI's prior written
        approval before engaging a Subcontractor to perform work on a MARI
        engagement, where required by the Supplier's Agreement.
      </p>

      <h2>29. Monitoring and Audit Rights</h2>
      <p>
        MARI may request information from, and, where provided for in the
        applicable Agreement, audit or inspect, a Supplier's operations,
        records, and facilities to assess compliance with this Code. Suppliers
        must cooperate with such requests, reviews, and investigations, and must
        not falsify records or refuse to disclose information reasonably
        requested by MARI in connection with this Code.
      </p>

      <h2>30. Reporting Misconduct; Non-Retaliation</h2>
      <p>
        Suppliers, and their personnel, are encouraged to promptly report any
        suspected violation of this Code, Applicable Law, or a MARI policy (an
        "Ethical Concern") through any of the following channels (the
        "Reporting Channels"):
      </p>
      <ul>
        <li>the Supplier's primary MARI business contact;</li>
        <li>
          <a href="mailto:legalnotices@marigrp.com"
            >legalnotices&#64;marigrp.com</a
          >; or
        </li>
        <li>
          in writing to AE EventsCo Holdings LLC, 8383 Wilshire Blvd, Suite 400,
          Beverly Hills, CA 90211, Attention: General Counsel.
        </li>
      </ul>

      <h2>Part VII – Glossary of Terms</h2>
      <p>
        <strong>"Agreement"</strong> means any master services agreement,
        purchase order, statement of work, license, or other written contract
        between a Supplier and MARI.
      </p>
      <p>
        <strong>"Anything of Value"</strong> includes cash, cash equivalents,
        gifts, meals, travel, entertainment, business opportunities, offers of
        employment, physical assets, discounts, charitable contributions,
        political donations, and other benefits, without regard to minimum or
        maximum amount.
      </p>
      <p>
        <strong>"Applicable Law"</strong> means all laws, statutes, regulations,
        and binding governmental requirements applicable to a Supplier's
        operations or its engagement with MARI. Applicable Law includes those
        laws that may be applicable to an engagement with MARI by virtue of the
        fact that it or its parent company is located in the United States or
        United Kingdom, including without limitation, the U.S. Foreign Corrupt
        Practices Act and the UK Bribery Act.
      </p>
      <p>
        <strong>"Bribery"</strong> means offering, promising, giving,
        requesting, or accepting Anything of Value, or any other advantage, to
        obtain an improper business advantage, improperly influence a decision,
        or assist in improperly obtaining or retaining business.
      </p>
      <p>
        <strong>"Business Hospitality"</strong> means participation in meals,
        events, recreational activities, or other entertainment with a business
        partner where both the host and the guest are present and business
        matters may be discussed. If the host is not present, the benefit will
        generally be considered a Gift. Business Hospitality does not include
        offering someone a glass of water, cup of coffee or other reasonable
        beverage during a meeting or visit.
      </p>
      <p>
        <strong>"Code"</strong> means this MARI Supplier Code of Conduct, as
        amended from time to time.
      </p>
      <p>
        <strong>“Data Subject”</strong> means the natural person about whom the
        Personal Data relates.
      </p>
      <p>
        <strong>"Gift"</strong> means anything of tangible or intangible value
        provided without the expectation of the giver's direct participation,
        including cash, gift cards, transportation, lodging, discounts,
        promotional items, or event tickets where the giver is not present.
      </p>
      <p>
        <strong>"Government Entity"</strong> means any national, provincial,
        state, or local government, including any department, agency, or
        instrumentality thereof; any state-owned or state-controlled company;
        any public international organization; or any entity that performs
        governmental functions.
      </p>
      <p>
        <strong>"Government Official"</strong> means any officer, employee, or
        representative of a Government Entity, including elected officials,
        government employees, employees of state-owned enterprises, officials of
        public international organizations, political party officials, and
        candidates for political office.
      </p>
      <p>
        <strong>"Immediate Family Member"</strong> includes a spouse or domestic
        partner, parents, children, siblings, mothers- and fathers-in-law, sons-
        and daughters-in-law, brothers- and sisters-in-law, or anyone who shares
        the same household.
      </p>
      <p>
        <strong>"Information Assets"</strong> means any information belonging
        to MARI, including intellectual property, confidential business
        information, and non-public data relating to employees, customers,
        Suppliers, or other business partners.
      </p>
      <p>
        <strong>"Intellectual Property or IP"</strong> refers to creations of
        the mind that are protected by law, including patents, inventions, trade
        secrets, trademarks, copyrights, designs, business names, domain names,
        software, and other proprietary information.
      </p>
      <p>
        <strong>“Modern Slavery”</strong> means human slavery, servitude, forced
        or compulsory labor, bonded or indentured labor, involuntary prison
        labor, debt bondage, forced marriage, child labor, and human
        trafficking. Modern slavery is the umbrella term that encompasses these
        various human rights issues.
      </p>
      <p>
        <strong>"Personal Data"</strong> means any information relating to an
        identified or identifiable individual, such as name, contact
        information, identification numbers, location data, online identifiers,
        financial information, or other information protected by Applicable
        Data Protection Law.
      </p>
      <p>
        <strong>“Process”</strong> (regardless of verb tense) means any
        operation or set of operations which is performed on personal data or on
        sets of personal data, whether or not by automated means, such as
        collection, recording, organization, structuring, storage, adaptation or
        alteration, retrieval, consultation, use, disclosure by transmission,
        dissemination or otherwise making available, alignment or combination,
        restriction, erasure or destruction.
      </p>
      <p>
        <strong>"Restricted Information"</strong> means information subject to
        additional protection under law, contract, or MARI policy. Examples may
        include Personal Data, trade secrets, confidential financial
        information, security credentials, or other sensitive Company
        information.
      </p>
      <p>
        <strong>"Sanctioned Party"</strong> means any individual or entity that
        is the target of economic sanctions administered by the U.S. Department
        of the Treasury's Office of Foreign Assets Control, the United Nations,
        the European Union, the United Kingdom, or another applicable sanctions
        authority, or that is owned or controlled by such a party.
      </p>
      <p>
        <strong>"Significant Financial Interest"</strong> means any ownership or
        financial interest that could influence, or appear to influence,
        business judgment. This may include ownership of 5% or more of the
        shares of a publicly traded company or any financial interest that could
        reasonably create the perception of a conflict of interest.
      </p>
      <p>
        <strong>"Subcontractor"</strong> means any third party to whom a
        Supplier delegates its obligations under an Agreement, or an affiliate
        of a Supplier that does not contract directly with MARI.
      </p>
    </div>
  </div>
</section>
